import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** One-time gate at first sign-in.
 *
 * Deliberately not a checkbox buried in the sign-in form. Google Play's UGC policy expects terms
 * to be accepted before someone can publish, and DPDP's notice requirement is that a person can
 * understand what they are agreeing to — neither is satisfied by a tickbox next to "Continue".
 *
 * The gate is enforced server-side too (`require_accepted_terms`), so this screen is the courteous
 * path, not the security boundary.
 */

const POINTS = [
  {
    title: "We are not the seller",
    body: "Apna Shahar connects you with local businesses. When you order or book, your contract is with that business — they are responsible for what they provide.",
  },
  {
    title: "We never handle your money",
    body: "You pay a business directly through your own UPI app. We never see your bank or card details, and a business confirms payment manually.",
  },
  {
    title: "In an emergency, call 108",
    body: "The ambulance feature passes your request to independent providers. It is not an emergency service and cannot guarantee a response.",
  },
  {
    title: "Be decent to other people",
    body: "Reviews and posts must be honest and about businesses you have actually dealt with. No abuse, no personal details, no paid reviews.",
  },
  {
    title: "You are 18 or over",
    body: "Apna Shahar is for adults. We do not ask your age or date of birth — only that you confirm this.",
  },
];

export default function AcceptTerms() {
  const { auth, refreshMe, t } = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!auth) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/auth/terms/accept");
      await refreshMe();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14" data-testid="accept-terms">
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Before you start</h1>
      <p className="mt-4 text-base leading-relaxed text-muted-foreground">
        A few things worth knowing. The full documents are linked below and you can read them at any
        time.
      </p>

      <div className="mt-8 space-y-4">
        {POINTS.map((point) => (
          <div key={point.title} className="rounded-xl border px-4 py-4">
            <h2 className="font-heading text-base font-semibold">{point.title}</h2>
            <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">{point.body}</p>
          </div>
        ))}
      </div>

      <p className="mt-8 text-[15px] leading-relaxed">
        By continuing you accept our{" "}
        <Link className="font-medium underline underline-offset-4" to="/terms" target="_blank">
          Terms of Service
        </Link>{" "}
        and confirm you have read the{" "}
        <Link className="font-medium underline underline-offset-4" to="/privacy" target="_blank">
          Privacy Notice
        </Link>
        .
      </p>

      <Button className="mt-6 w-full sm:w-auto" disabled={busy} onClick={accept} data-testid="accept-terms-btn">
        {busy ? "…" : "I agree — continue"}
      </Button>
    </div>
  );
}
